import {
  ComponentLoader,
  GeneralSelect,
  InputWithIcon,
  ButtonWithIcon,
} from '@clickcease/cc-client-components-lib'
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import React, { useMemo } from 'react'

import { InputContainer, TagListContainer, TagsSelectionRow, Prefix } from './Connector.styled'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { useTranslation } from 'react-i18next'
import { useGoogleAnalyticsConnections } from '../api/getGoogleAnalyticsConnections'
import { GAConnectionObject } from 'Types/GoogleAnalytics'
import { ConnectionActionButton } from './ConectionActionButton'
import { useDeleteGoogleAnalyticsConnection } from '../api/deleteGoogleAnalyticsConnection'
import { useSetGoogleAnalyticsConnection } from '../api/addGoogleAnalyticsConnection'
import {
  GoogleAnalyticsConnectionWithStateId,
  createDefaultConnection,
  findDomainNameByTagId,
  removePrefix,
} from './Connector.utils'
import { validateInputOnlyNumbers } from 'Utils/validation'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

export const ConnectionsList = () => {
  const { t } = useTranslation('googleAnalytics')
  const { data: domainsData, isLoading } = useGetDomains()
  const { data: existingConnections } = useGoogleAnalyticsConnections()
  const { mutate: deleteConnection, isLoading: isDeleting } = useDeleteGoogleAnalyticsConnection()
  const { mutate: saveConnection, isLoading: isSaving } = useSetGoogleAnalyticsConnection()
  const [createdConnections, setCreatedConnections] = React.useState<
    GoogleAnalyticsConnectionWithStateId[]
  >(() => [createDefaultConnection()])
  const [emptyFieldError, setEmptyFieldError] = React.useState<{
    [key: string]: 'tagId' | 'trackingId'
  }>({})

  const domains = useMemo(() => {
    return !domainsData?.data.domains
      ? []
      : domainsData.data.domains.map((domain) => ({
          value: domain.tagId.toString(),
          label: domain.address,
        }))
  }, [domainsData])

  const addConnection = () => {
    setCreatedConnections((prev) => [...prev, createDefaultConnection()])
  }

  const removeConnection = (stateId: string) => {
    setCreatedConnections((prev) => prev.filter((item) => item.stateId !== stateId))
  }

  const onDeleteConnection = (connectionId: string) => () => {
    const connection = existingConnections.find((c) => c.trackingId === connectionId)
    if (connection?.tagId) {
      deleteConnection({ tagId: connection.tagId.toString(), trackingId: connectionId })
    }
  }

  const onSaveConnection = (stateId: string) => () => {
    const connection = createdConnections.find((c) => c.stateId === stateId)
    if (connection && connection.tagId && connection.trackingId) {
      const savingObject = { trackingId: 'G-' + connection.trackingId, tagId: connection.tagId }
      saveConnection(
        { connections: [savingObject] as GAConnectionObject[] },
        {
          onSuccess: (data) => {
            if (!(data instanceof Error)) removeConnection(stateId)
          },
        }
      )
    } else if (connection && (!connection.tagId || !connection.trackingId)) {
      setEmptyFieldError((prev) => ({
        ...prev,
        [stateId]: !connection.tagId ? 'tagId' : 'trackingId',
      }))
    }
  }

  const findConnectionIndex = (connectionId: string) => {
    return createdConnections.findIndex((item) => item.stateId === connectionId)
  }

  const setConnectionTrackingId =
    (connectionId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const stringIsValid = validateInputOnlyNumbers(e)
      if (stringIsValid) {
        setCreatedConnections((prev) => {
          const index = findConnectionIndex(connectionId)
          if (index === -1) return prev
          const newTags = [...prev]
          newTags[index].trackingId = removePrefix(e.target.value)
          return newTags
        })
      }
    }

  const setConnectionDomainId =
    (connectionId: string) => (option: { value: string; label: string } | null) => {
      if (option?.value) {
        setCreatedConnections((prev) => {
          const index = findConnectionIndex(connectionId)
          if (index === -1) return prev
          const newTags = [...prev]
          newTags[index].tagId = Number(option.value)
          return newTags
        })
      }
    }

  return (
    <>
      <TagListContainer>
        {isLoading ? (
          <ComponentLoader css={{ width: '100px', height: '100px', position: 'relative' }} />
        ) : (
          [
            ...(existingConnections as unknown as GoogleAnalyticsConnectionWithStateId[]),
            ...createdConnections,
          ].map((opt, index) => (
            <TagsSelectionRow key={`GA4SelectionRow${index}`}>
              <InputContainer>
                <GeneralSelect
                  selectCss={{ width: '276px' }}
                  value={{
                    value: opt.tagId ? opt.tagId.toString() : '',
                    label: findDomainNameByTagId(opt.tagId || 0, domains),
                  }}
                  options={domains}
                  isDisabled={!opt.stateId}
                  onChange={setConnectionDomainId(opt.stateId || opt.trackingId)}
                />
              </InputContainer>
              <InputContainer>
                <InputWithIcon
                  iconLeft={<Prefix>G-</Prefix>}
                  css={{ width: '276px', paddingLeft: '25px' }}
                  value={removePrefix(opt.trackingId)}
                  onChange={setConnectionTrackingId(opt.stateId || opt.trackingId)}
                  disabled={!opt.stateId}
                  error={
                    emptyFieldError[opt.stateId || opt.trackingId] &&
                    emptyFieldError[opt.stateId || opt.trackingId] === 'trackingId'
                  }
                />
              </InputContainer>
              <ConnectionActionButton
                isExistingConnection={!opt.stateId}
                isLoading={isDeleting || isSaving}
                onClick={
                  !opt.stateId ? onDeleteConnection(opt.trackingId) : onSaveConnection(opt.stateId)
                }
              />
            </TagsSelectionRow>
          ))
        )}
      </TagListContainer>
      <ButtonWithIcon
        text={t('connector.add')}
        onClick={addConnection}
        iconLeft={<FontAwesomeIcon icon={faPlusCircle as IconProp} />}
        variant="ghost"
      />
    </>
  )
}
